import { FLAG_NAMES, flagIsEnabled } from "lib/environment";
import greenhouseLogo from "pages/pmt/integrations/images/integration-partner-logos/greenhouse-logo.svg";
import jazzHrLogo from "pages/pmt/integrations/images/integration-partner-logos/jazzhr-logo.svg";
import leverLogo from "pages/pmt/integrations/images/integration-partner-logos/lever-logo.svg";
import multipleAtsLogo from "pages/pmt/integrations/images/integration-partner-logos/multiple-ats-logo.png";
import {
  AtsPendingHireDetails,
  CompanyAdmin,
  PmtIntegration,
  PmtIntegrationAlertsDetails,
} from "types";
import {
  PmtIntegrationInstancesByKey,
  PmtIntegrationPdpIntegration,
  PmtIntegrationSetupIntegration,
} from "types/pmt-integrations";
import { PERMISSION } from "types/RequiredPermissions";
import { DEFAULT_PMT_INTEGRATION_PARAMS } from "./constants";

type PartnerLogos = {
  partnerLogos: {
    [key: string]: string;
  };
};

export const pmtAlertTypes = {
  needsWarningOverlay: {
    pmtIntegrationAuthError: "pmt_integration_auth_error",
  },
};

export const integrationLogos: PartnerLogos = {
  partnerLogos: {
    ats: multipleAtsLogo,
    greenhouse: greenhouseLogo,
    jazzhr: jazzHrLogo,
    lever: leverLogo,
  },
};

export const createPendingHireNotification = (item: AtsPendingHireDetails) => {
  const partnerName = item.type.toLowerCase().split("_")[0];
  const photoUrl = integrationLogos.partnerLogos[partnerName];

  // Makeshift "member" object to pass logo url to avatar to render logos for pending hire alerts
  const avatar = {
    firstName: "",
    friendlyFirstName: "",
    name: "",
    lastName: partnerName,
    photoUrl: photoUrl,
    uuid: "",
    preferredName: partnerName,
  };

  return {
    alertNotificationProps: {
      key: item.url,
      message: item.message,
      url: item.url,
      member: avatar,
      priority: item.priority,
    },
  };
};

export const createPmtIntegrationAlertsNotification = (
  item: PmtIntegrationAlertsDetails
) => {
  const photoUrl = integrationLogos.partnerLogos[item.integrationName];

  // Makeshift "member" object to pass logo url to avatar to render logos
  const avatar = {
    firstName: "",
    friendlyFirstName: "",
    name: "",
    lastName: item.integrationName,
    photoUrl: photoUrl,
    uuid: Object.values(pmtAlertTypes.needsWarningOverlay).includes(item.type)
      ? "warning_overlay"
      : "",
    preferredName: item.integrationName,
  };

  return {
    alertNotificationProps: {
      key: item.url,
      message: item.message,
      url: item.url,
      member: avatar,
      priority: item.priority,
    },
  };
};

// NOTE: this relies on integration feature flag names having the format {INTEGRATION_NAME_UPPERCASE}_ENABLED
// if there is no flag set, this will return false - so the integration WILL appear in the marketplace
// this is done so that TMS BE can control the visibility of its own integrations in the marketplace (via launch darkly)
export const isPmtIntegrationDisabled = (
  name?: string,
  companyUuid?: string
) => {
  if (!companyUuid || !name) return false;
  const flagNameKey = `${name.toUpperCase()}_ENABLED`;
  const flagName = FLAG_NAMES[flagNameKey as keyof typeof FLAG_NAMES];
  const flagValue = flagIsEnabled(flagName, companyUuid);
  if (flagValue === false) {
    return true;
  }
  return false;
};

export const getPmtIntegrationIsConnectionStatus = (
  integration:
    | PmtIntegration
    | PmtIntegrationPdpIntegration
    | PmtIntegrationSetupIntegration
    | undefined
) => {
  const instancesByKey = integration?.instancesByKey;

  if (!integration || integration?.status === "inactive") return "DISCONNECTED";
  if (instanceError(instancesByKey)) return "ERROR";
  if (integration.status === "active") return "CONNECTED";
  if (integration.status === "not_started") return "PENDING";
  return "DISCONNECTED";
};

export const instanceError = (
  instancesByKey: PmtIntegrationInstancesByKey[] | undefined | null
) => {
  return instancesByKey?.some(
    (instanceByKey) => instanceByKey.instances[0]?.status === "error"
  );
};

export const userPermissionCheck = (
  requiredPermissions: PERMISSION[],
  userPermissions: PERMISSION[] | undefined
) => {
  if (!userPermissions) return false;
  // Changed this back to .some instead of .every because we want to check if
  // the user has at least one of the required permissions, since we dont have a
  // concept of umbrella permissions yet and admins with either pay_metrics or
  // make_payments permissions should have access
  return requiredPermissions.some((permission) =>
    userPermissions?.includes(permission)
  );
};

export const companyAdminsWithPermissionCheck = (
  requiredPermissions: PERMISSION[],
  companyAdmins: CompanyAdmin[] | undefined
) => {
  if (!companyAdmins) return [];
  const adminsWithPermissions: string[] = [];
  companyAdmins.map((admin) => {
    const permissions = admin.permissions;
    const hasPermission = userPermissionCheck(requiredPermissions, permissions);
    if (hasPermission) {
      adminsWithPermissions.push(admin.friendlyFullName);
    }
  });
  return adminsWithPermissions;
};

export const listAdminPermissions = (
  requiredPermissions: PERMISSION[],
  permissionsMap: { [key: number]: string }
) => {
  const permissionDescriptions: string[] = [];
  if (!permissionsMap || !requiredPermissions) return [];
  requiredPermissions.map((permission) => {
    const permissionDescription = permissionsMap[permission];
    permissionDescriptions.push(permissionDescription);
  });
  return permissionDescriptions;
};

export const setPmtIntegrationVariables = () => {
  const options = {
    ...DEFAULT_PMT_INTEGRATION_PARAMS,
  };

  return {
    integration_status: options.integration_status,
    integration_name: options.integration_name,
    integration_instance_status: options.integration_instance_status,
    integration_instance_keys: options.integration_instance_keys,
    limit: options.limit,
  };
};

export const checkPmtIntegrationsEnabled = (companyId: string | undefined) =>
  flagIsEnabled(FLAG_NAMES.PMT_INTEGRATIONS, companyId);
